import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./bodyContainer.css";
import SideBar from "../Components/SideBar";
import { useTranslation } from "react-i18next";
import ReactQuill from "react-quill";
import { role } from "../Constants/role";
import "react-quill/dist/quill.snow.css";
import { validateURL } from "../Components/util/linkValidation.js";
import axios from "axios";

import {
  getListingsById,
  postListingsData,
  updateListingsData,
  uploadMedia,
  deleteMedia,
  uploadToSignedUrl,
} from "../Services/listingsApi";
import {
  getCanvasBlobFromPdf,
  generateThumbnails,
  prepareOrderedUploadImages,
} from "../Services/mediaUtils.js";

import { getProfile } from "../Services/usersApi";
import { getCities, getCitiesByUserId } from "../Services/citiesApi";

import FormData from "form-data";
import Alert from "../Components/Alert";
import SearchableDropdown from "../Components/SearchableDropdown";
import { hiddenCategories } from "../Constants/hiddenCategories";
import FormImage from "./FormImage";
import { UploadSVG } from "../assets/icons/upload";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_blue.css";
import { format, isValid } from "date-fns";
import { status } from "../Constants/status";
import errorCodes from "../Constants/errorCodes";
import mediaType from "../Constants/mediaType.js";
import Delta from "quill-delta";
import { Combobox } from "@headlessui/react";

// Roles that fetch cities via the /cities/{userId}/cityAdmin endpoint
// instead of the full /cities list: administrator (1), TerminalAdmin (4),
// superCityAdmin (6).
const CITY_ADMIN_ROLES = [
  role.administrator,
  role.TerminalAdmin,
  role.superCityAdmin,
];

function UploadListings() {
  const { t } = useTranslation();
  const editor = useRef(null);
  const [listingId, setListingId] = useState(0);
  const [newListing, setNewListing] = useState(true);
  const [updating, setUpdating] = useState(false);

  const [image, setImage] = useState([]);
  const [pdf, setPdf] = useState(null);
  const [localImageOrPdf, setLocalImageOrPdf] = useState(false);
  const [isAdmin] = useState(false);
  const CHARACTER_LIMIT_TITLE = 255;
  const CHARACTER_LIMIT_DESCRIPTION = 65535;
  const [successMessage, setSuccessMessage] = useState("");
  const [isSuccess, setIsSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [categories, setCategories] = useState([]);
  const [isFormValid, setIsFormValid] = useState(false);
  const navigate = useNavigate();
  const [mediaToDelete, setMediaToDelete] = useState([]);
  const [userRole, setUserRole] = useState(role.User);
  const [cities, setCities] = useState([]);
  const [selectedCities, setSelectedCities] = useState([]);
  const [categoryId, setCategoryId] = useState(0);
  const [subCategories, setSubCategories] = useState([]);
  const [childrenId, setChildrenId] = useState(0);
  const [hideMediaToggle, setHideMediaToggle] = useState(false);
  const queryCityNameRef = useRef(null);
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);

  useEffect(() => {
    const accessToken =
      window.localStorage.getItem("accessToken") ||
      window.sessionStorage.getItem("accessToken");
    const refreshToken =
      window.localStorage.getItem("refreshToken") ||
      window.sessionStorage.getItem("refreshToken");

    if (!accessToken && !refreshToken) {
      navigate("/login");
    }
  }, [navigate]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const profileResponse = await getProfile();

        setUserRole(profileResponse.data.data.roleId);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, []);

  function handleDragEnter(e) {
    e.preventDefault();
    e.stopPropagation();
  }

  function handleDragLeave(e) {
    e.preventDefault();
    e.stopPropagation();
  }

  function handleDragOver(e) {
    e.preventDefault();
    e.stopPropagation();
  }

  function handleDrop(e) {
    e.preventDefault();
    e.stopPropagation();

    const MAX_SIZE_BYTES = 20 * 1024 * 1024;
    const files = Array.from(e.dataTransfer.files);
    if (files.length === 0) return;

    let hasImage = false;
    let hasPdf = false;
    let validFiles = [];

    for (let file of files) {
      const name = file.name.toLowerCase();
      const size = file.size;
      const type = file.type;

      // Block >20MB
      if (size > MAX_SIZE_BYTES) {
        alert(t("fileTooLargeAlert", { file: file.name, maxSize: "20MB" }));
        continue;
      }

      // When hideMediaToggle is true, user can upload images only (no PDFs)
      if (type === "application/pdf" && hideMediaToggle) {
        alert(t("fileNotImage", { fileName: file.name }));
        continue;
      }

      if (type.startsWith("image/")) {
        hasImage = true;
      } else if (type === "application/pdf") {
        hasPdf = true;
      }

      validFiles.push(file);
    }

    // Block mixing
    if (hasImage && (pdf || hasPdf)) {
      alert(t("cannotUploadImagesWithPdf"));
      return;
    }

    if (hasPdf && (image?.length > 0 || hasImage)) {
      alert(t("cannotUploadPdfWithImages"));
      return;
    }

    if (validFiles.length === 0) return;

    setLocalImageOrPdf(true);

    if (hasImage) {
      const remainingSlots = 8 - (image?.length || 0);
      validFiles = validFiles.slice(0, remainingSlots);
      setImage((prev) => [...prev, ...validFiles]);
      setListingInput((prev) => ({ ...prev, hasAttachment: true }));
    }

    if (hasPdf) {
      setPdf(validFiles[0]);
      setListingInput((prev) => ({ ...prev, hasAttachment: true }));
    }
  }

  function handleInputChange(e) {
    e.preventDefault();

    const MAX_SIZE_MB = 20;
    const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024;

    let files = Array.from(e.target.files);
    e.target.value = ""; // ✅ Reset input to allow same file selection again

    const remainingSlots = 8 - (image?.length || 0);
    files = files.slice(0, remainingSlots);

    const validFiles = [];
    let hasImage = false;
    let hasPdf = false;

    for (let file of files) {
      const name = file.name.toLowerCase();
      const size = file.size;
      const type = file.type;

      // Block >20MB
      if (size > MAX_SIZE_BYTES) {
        alert(t("fileTooLarge", { fileName: file.name }));
        continue;
      }

      // When hideMediaToggle is true, user can upload images only (no PDFs)
      if (type === "application/pdf" && hideMediaToggle) {
        alert(t("fileNotImage", { fileName: file.name }));
        continue;
      }

      // Detect file type
      if (type.startsWith("image/")) {
        hasImage = true;
      } else if (type === "application/pdf") {
        hasPdf = true;
      }

      validFiles.push(file);
    }

    // Block mixing image + PDF
    if (hasImage && (pdf || hasPdf)) {
      alert(t("cannotUploadImagesWithPdf"));
      return;
    }

    if (hasPdf && (image?.length || hasImage)) {
      alert(t("cannotUploadPdfWithImages"));
      return;
    }
    if (validFiles.length === 0) return;

    setLocalImageOrPdf(true);

    if (hasImage) {
      setImage((prev) => [...prev, ...validFiles]);
      setListingInput((prev) => ({ ...prev, hasAttachment: true }));
    }

    if (hasPdf) {
      setPdf(validFiles[0]);
      setListingInput((prev) => ({ ...prev, hasAttachment: true }));
    }
  }

  const handleAdditionalInfoChange = (e) => {
    const { name, value } = e.target;
    setListingInput((prevState) => ({
      ...prevState,
      additionalInformation: {
        ...prevState.additionalInformation,
        [name]: value,
      },
    }));
    validateInput({ target: { name, value } });
  };

  const [localImages, setLocalImages] = useState([]);
  const MAX_IMAGES = 8;

  const handleMultipleInputChange = (event) => {
    let newFiles = Array.from(event.target.files).slice(0, MAX_IMAGES);
    event.target.value = ""; // ✅ Reset input to allow same file selection again

    const MAX_SIZE_BYTES = 20 * 1024 * 1024;

    const validImages = [];
    let hasInvalidFile = false;

    for (const file of newFiles) {
      if (file.size > MAX_SIZE_BYTES) {
        alert(t("fileExceedsLimit", { fileName: file.name }));
        hasInvalidFile = true;
        continue;
      }

      if (!file.type.startsWith("image/")) {
        alert(t("fileNotImage", { fileName: file.name }));
        hasInvalidFile = true;
        continue;
      }

      validImages.push(file);
    }

    if (hasInvalidFile) return;

    const total = image.length + validImages.length;
    if (total > MAX_IMAGES) {
      alert(t("listingImageNumberAlert", { limit: MAX_IMAGES }));
      return;
    }

    setLocalImages((prev) => [...prev, ...validImages]);
    setImage((prev) => [...prev, ...validImages]);
  };

  const handleUpdateMultipleInputChange = (e) => {
    const MAX_IMAGES = 8;
    const MAX_SIZE_BYTES = 20 * 1024 * 1024;

    const currentImageCount = image?.length || 0;
    const selectedFiles = Array.from(e.target.files);

    e.target.value = ""; // reset input to allow reselecting same files

    const totalAfterUpload = currentImageCount + selectedFiles.length;

    // ✅ Early alert if total exceeds max
    if (totalAfterUpload > MAX_IMAGES) {
      alert(t("listingImageNumberAlert", { limit: MAX_IMAGES }));
      return;
    }

    const validImages = [];
    let hasInvalidFile = false;

    for (const file of selectedFiles) {
      if (file.size > MAX_SIZE_BYTES) {
        alert(t("fileExceedsLimit", { fileName: file.name }));
        hasInvalidFile = true;
        continue;
      }

      if (!file.type.startsWith("image/")) {
        alert(t("fileNotImage", { fileName: file.name }));
        hasInvalidFile = true;
        continue;
      }

      validImages.push(file);
    }

    if (hasInvalidFile) return;

    setLocalImages((prev) => [...prev, ...validImages]);
    setImage((prev) => [...prev, ...validImages]);
    setLocalImageOrPdf(true);
    setListingInput((prev) => ({ ...prev, hasAttachment: true }));
  };

  function handleRemoveImage(mediaId) {
    if (listingId && mediaId) {
      setMediaToDelete((prev) => [...prev, mediaId]);

      setImage((prevImages) => prevImages.filter((img) => img.id !== mediaId));
    }
  }

  function handleRemovePDF() {
    if (listingId && pdf?.id) {
      deleteMedia(listingId, [pdf.id])
        .then(() => {
          console.log("PDF successfully deleted from server:", pdf.id);
        })
        .catch((error) => {
          console.error("Error deleting PDF from server:", error);
        });
    }

    setPdf(null);
    setListingInput((prev) => ({
      ...prev,
      removePdf: true,
      pdf: null,
      hasAttachment: false,
    }));
  }

  const [listingInput, setListingInput] = useState({
    categoryId: 0,
    childrenId: 0,
    cityIds: [],
    statusId: 1,
    title: "",
    phone: "",
    website: "",
    email: "",
    description: "",
    logo: null,
    pdf: null,
    zipcode: "",
    latitude: "",
    longitude: "",
    address: "",
    removeImage: false,
    removePdf: false,
    hasImage: false,
    hasAttachment: false,
    additionalInformation: {
      price: "",
      startDate: "",
      endDate: "",
      discountPrice: "",
      expiryDate: "",
    },
  });

  const [error, setError] = useState({
    categoryId: "",
    childrenId: "",
    title: "",
    description: "",
    cityIds: "",
    cityAlreadySelected: "",
    startDate: "",
    endDate: "",
    latitude: "",
    longitude: "",
    address: "",
    expiryDate: "",
    website: "",
  });

  const handleSubmit = async (event) => {
    event.preventDefault();
    let valid = true;
    const newError = { ...error };
    
    // Validate address, latitude, and longitude if POI mode is enabled
    if (hideMediaToggle) {
      if (!listingInput.address || listingInput.address.trim() === "") {
        newError.address = t("fieldRequired");
        valid = false;
      } else {
        newError.address = "";
      }
      
      if (!listingInput.latitude || String(listingInput.latitude).trim() === "") {
        newError.latitude = t("fieldRequired");
        valid = false;
      } else {
        const lat = parseFloat(listingInput.latitude);
        if (isNaN(lat)) {
          newError.latitude = t("pleaseEnterValidLatitude");
          valid = false;
        } else if (lat < -90 || lat > 90) {
          newError.latitude = t("latitudeRangeError");
          valid = false;
        } else {
          newError.latitude = "";
        }
      }
      
      if (!listingInput.longitude || String(listingInput.longitude).trim() === "") {
        newError.longitude = t("fieldRequired");
        valid = false;
      } else {
        const lng = parseFloat(listingInput.longitude);
        if (isNaN(lng)) {
          newError.longitude = t("pleaseEnterValidLongitude");
          valid = false;
        } else if (lng < -180 || lng > 180) {
          newError.longitude = t("longitudeRangeError");
          valid = false;
        } else {
          newError.longitude = "";
        }
      }
    }
    
    for (const key in error) {
      // Skip description validation if hideMediaToggle is true
      if (key === "description" && hideMediaToggle) {
        continue;
      }

      let value;

      if (
        [
          "startDate",
          "endDate",
          "expiryDate",
          "price",
          "discountPrice",
        ].includes(key)
      ) {
        value = listingInput.additionalInformation[key];
      } else {
        value = listingInput[key];
      }
      const errorMessage1 = getErrorMessage(key, value);
      newError[key] = errorMessage1;
      if (key && error[key] && error[key].length > 0) {
        valid = false;
        newError[key] = value;
        return;
      }
      if (errorMessage1) {
        valid = false;
        setError(newError);
      }
    }
    setError(newError);
    if (!valid) {
      return;
    }

    if (valid) {
      setUpdating(true);

      try {
        const cityIdsToSubmit = selectedCities?.map((city) => city.id);
        const dataToSubmit = {
          ...listingInput,
          cityIds: cityIdsToSubmit,
          // Make description null-safe before using replace
          description: (listingInput.description || "").replace(/\n/g, "<br>"),
        };

        // If POI is enabled, send type = 'POI' and address in the payload. Otherwise, don't send type.
        if (hideMediaToggle) {
          dataToSubmit.type = "poi";
          dataToSubmit.address = listingInput.address;
        }

        const categoryId = dataToSubmit.categoryId;

        if (categoryId === 1) {
          dataToSubmit.additionalInformation.startDate = null;
          dataToSubmit.additionalInformation.endDate = null;
        } else if (categoryId === 2) {
          dataToSubmit.additionalInformation.expiryDate = null;
        } else {
          dataToSubmit.additionalInformation.startDate = null;
          dataToSubmit.additionalInformation.endDate = null;
          dataToSubmit.additionalInformation.expiryDate = null;
        }
        if (listingInput.childrenId)
          dataToSubmit.categoryId = listingInput.childrenId;
        let currentListingId = [];
        // let cityIdsArray = [];
        // if (response && response.data && response.data.data && Array.isArray(response.data.data) && response.data.data.length > 0) {
        //   currentListingId = response.data.data?.map(item => item.listingId);
        //   cityIdsArray = response.data.data?.map(item => item.cityId);
        // } else {
        //   console.error("Invalid response structure. Response:", response);
        //   throw new Error("Unable to retrieve listing and city IDs");
        // }

        if (mediaToDelete.length > 0) {
          await deleteMedia(listingId, mediaToDelete);
          setMediaToDelete([]); // Clear the mediaToDelete state after successful deletion
        }
        // Upload new media (images or PDF). In POI mode (hideMediaToggle = true),
        // only images are allowed by the UI, so this will only send images.
        if (localImageOrPdf) {
          const uploadedMediaType =
            image.length > 0 ? mediaType.IMAGE : mediaType.PDF;
          let filesToUpload = [];

          if (uploadedMediaType === mediaType.IMAGE) {
            filesToUpload = await prepareOrderedUploadImages(image);
          } else {
            filesToUpload = [pdf];
          }

          const numberOfMedia = filesToUpload.length;

          // const listingTargetId = newListing ? currentListingId[0] : listingId;

          // const { data } = await uploadMedia(listingTargetId, uploadedMediaType, numberOfMedia);
          const formdata = new FormData();
          // formdata.append('media', filesToUpload); // Append each file to the 'media' key
          filesToUpload?.forEach((file) => {
            formdata.append("media", file); // Append each file to the 'media' key
          });
          formdata.append("mediaType", uploadedMediaType);
          formdata.append("numberOfMedia", numberOfMedia);
          formdata.append("data", JSON.stringify(dataToSubmit));
          const response = newListing
            ? await postListingsData(formdata)
            : await updateListingsData(formdata, listingId);
          // if (response && response.data && response.data.data && Array.isArray(response.data.data) && response.data.data.length > 0) {
          //   currentListingId = response.data.data?.map(item => item.listingId);
          //   cityIdsArray = response.data.data?.map(item => item.cityId);
          // } else {
          //   console.error("Invalid response structure. Response:", response);
          //   throw new Error("Unable to retrieve listing and city IDs");
          // }
          // const { data } = await uploadMedia(listingTargetId, formdata);
          // const { uploadUrls, thumbnailUrls } = data.data;

          // const uploadTasks = [];

          // // Upload media files
          // filesToUpload?.forEach((file, idx) => {
          //   uploadTasks.push(uploadToSignedUrl(uploadUrls[idx], file));
          // });

          // // Handle thumbnails (PDF or first image)
          // if (thumbnailUrls) {
          //   let sourceImage;

          //   if (uploadedMediaType === mediaType.PDF) {
          //     sourceImage = await getCanvasBlobFromPdf(pdf);
          //   } else if (uploadedMediaType === mediaType.IMAGE && image.length > 0) {
          //     sourceImage = image[0];
          //   }

          //   if (sourceImage) {
          //     const { highQuality, lowQuality } = await generateThumbnails(sourceImage);

          //     uploadTasks.push(
          //       uploadToSignedUrl(thumbnailUrls.thumbnailHighUrl, highQuality),
          //       uploadToSignedUrl(thumbnailUrls.thumbnailLowUrl, lowQuality)
          //     );
          //   }
          // }

          // // Run all uploads in parallel
          // await Promise.all(uploadTasks);
        } else {
          // If no media is uploaded, we still need to submit the listing data
          const formdata = new FormData();
          formdata.append("data", JSON.stringify(dataToSubmit));
          const response = newListing
            ? await postListingsData(formdata)
            : await updateListingsData(formdata, listingId);
        }
        isAdmin
          ? setSuccessMessage(t("listingUpdatedAdmin"))
          : newListing
          ? setSuccessMessage(t("listingCreated"))
          : setSuccessMessage(t("listingUpdated"));

        setIsSuccess(true);
        setTimeout(() => {
          setSuccessMessage(false);
          const params = new URLSearchParams(window.location.search);
          const status = params.get("status");
          const statusQuery = status ? `?status=${status}` : "";
          const from = params.get("from") || "/Dashboard"; // fallback
          navigate(`${from}${statusQuery}`);
        }, 5000);
      } catch (error) {
        // const { errorCode } = error.response?.data || {};

        // let userMessage;

        // if (errorCode === 8100) {
        //   userMessage = t("categoryNotAvailableForSelectedCities");
        // } else {
        //   userMessage = errorCodes[errorCode] || t("changesNotSaved");
        // }
        if (axios.isAxiosError(error)) {
          const message = error.response?.data?.message || t("changesNotSaved");
          setErrorMessage(message);
        } else {
          setErrorMessage(t("changesNotSaved"));
          setTimeout(() => setErrorMessage(false), 5000);
        }
      } finally {
        setUpdating(false);
      }
    } else {
      setErrorMessage(t("invalidData"));
      setSuccessMessage(false);
      setTimeout(() => setErrorMessage(false), 5000);
    }
  };

  const handleCancel = () => {
    setMediaToDelete([]);
    const params = new URLSearchParams(window.location.search);
    const status = params.get("status");
    const from = params.get("from") || "/Dashboard"; // fallback
    navigate(status ? `${from}?status=${status}` : from);
  };

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const listingId = searchParams.get("listingId");
    if (listingId) {
      setNewListing(false);
    }
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      const searchParams = new URLSearchParams(window.location.search);
      const accessToken =
        window.localStorage.getItem("accessToken") ||
        window.sessionStorage.getItem("accessToken");
      const refreshToken =
        window.localStorage.getItem("refreshToken") ||
        window.sessionStorage.getItem("refreshToken");

      if (!accessToken && !refreshToken) {
        navigateTo("/login");
        return;
      }

      const listingId = searchParams.get("listingId");

      try {
        const profileResponse = await getProfile();
        const roleId = profileResponse?.data?.data?.roleId;
        const citiesResponse = CITY_ADMIN_ROLES.includes(roleId)
          ? await getCitiesByUserId()
          : await getCities();
        const citiesData = citiesResponse?.data?.data || [];
        const sortedCities = [...citiesData].sort((a, b) =>
          a.name.localeCompare(b.name)
        );
        setCities(sortedCities);

        if (listingId) {
          setListingId(parseInt(listingId));
          setNewListing(false);

          const listingsResponse = await getListingsById(listingId);
          const listingData = listingsResponse.data.data;

          const selectedCategory = categories.find(
            (cat) => cat.id === listingData.categoryId
          );
          if (selectedCategory?.children?.length > 0) {
            setSubCategories(selectedCategory.children);
          }
          setChildrenId(listingData.childrenId || 0);

          const cityIds = listingData.allCities || [];
          if (cityIds.length > 0) {
            const selectedCitiesFromBackend = citiesData.filter((city) =>
              cityIds.includes(city.id)
            );
            const allCategories = selectedCitiesFromBackend.flatMap(
              (c) => c.categoryData || []
            );
            const uniqueCategories = Array.from(
              new Map(allCategories?.map((cat) => [cat.id, cat])).values()
            );
            const visibleCategories = uniqueCategories.filter(
              (cat) => !hiddenCategories.includes(cat.id)
            );
            setCategories(visibleCategories);

            setSelectedCities(selectedCitiesFromBackend);
          }
          if (
            !process.env.REACT_APP_MULTIPLECITYSELECTION ||
            process.env.REACT_APP_MULTIPLECITYSELECTION === "False"
          ) {
            const singleCity = citiesData.find(
              (city) => city.id === listingData.cityId
            );
            if (singleCity) setSelectedCities([singleCity]);
          }
          setHideMediaToggle(listingData.type === "poi" ? true : false);

          setListingInput({
            ...listingInput,
            cityIds: cityIds.length > 0 ? cityIds : [listingData.cityId],
            title: listingData.title || "",
            categoryId: listingData.categoryId,
            childrenId: listingData.childrenId || 0,
            description: listingData.description,
            latitude: listingData.latitude,
            longitude: listingData.longitude,
            address: listingData.address || "",
            zipcode: listingData.zipCode,
            phone: listingData.phone,
            website: listingData.website,
            email: listingData.email,
            additionalInformation: {
              price: listingData.additionalInformation?.price,
              startDate: listingData.additionalInformation?.startDate,
              endDate: listingData.additionalInformation?.endDate,
              expiryDate: getUTCTime(
                listingData.additionalInformation?.expiryDate
              ),
              discountPrice: listingData.additionalInformation?.discountPrice,
            },
          });
          
          // Populate address search field if address exists
          if (listingData.address) {
            setQuery(listingData.address);
            // Create a location object for selectedLocation if needed
            if (listingData.latitude && listingData.longitude) {
              setSelectedLocation({
                display_name: listingData.address,
                lat: listingData.latitude,
                lon: listingData.longitude,
              });
            }
          }
          
          setDescription(listingData.description);
          setCategoryId(listingData.categoryId);

          if (listingData.media && listingData.media.length > 0) {
            const sortedLogos = listingData.media
              .sort((a, b) => a.mediaOrder - b.mediaOrder)
              ?.map((media) => ({
                path: media.signedUrl,
                id: media.id,
                mediaType: media.mediaType,
              }));

            const images = sortedLogos.filter((media) => media.mediaType === 1);
            const pdfs = sortedLogos.filter((media) => media.mediaType === 2);

            setImage(images);
            if (pdfs.length > 0) {
              setPdf({
                link: pdfs[0].path,
                id: pdfs[0].id,
                name: pdfs[0].path.split("/").pop(),
              });
            }
          }
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, [listingId]);

  function categoryDescription(category) {
    if (category === "4") {
      return "clubsDescription";
    } else if (category === "10") {
      return "companyPortraitsDescription";
    } else {
      return "";
    }
  }

  const onInputChange = (e) => {
    const { name, value } = e.target;
    if (name === "title" && value.length > CHARACTER_LIMIT_TITLE) {
      setError((prev) => ({
        ...prev,
        title: t("characterLimitExceeded", {
          limit: CHARACTER_LIMIT_TITLE,
          count: value.length,
        }),
      }));
      return;
    } else {
      setListingInput((prev) => ({
        ...prev,
        [name]: value,
      }));
    }

    validateInput(e);
  };

  const navigateTo = (path) => {
    if (path) {
      navigate(path);
    }
  };

  const [description, setDescription] = useState("");

  useEffect(() => {
    const quill = editor.current?.getEditor();
    if (quill) {
      quill.clipboard.addMatcher("IMG", (node) => {
        const imageUrl = node.getAttribute("src");
        return new Delta().insert(imageUrl || "[Image]");
      });
    }
  }, []);

  const onDescriptionChange = (newContent) => {
    const hasNumberedList = newContent.includes("<ol>");
    const hasBulletList = newContent.includes("<ul>");
    let descriptions = [];
    let listType = "";

    const plainText = newContent.replace(/(<([^>]+)>)/gi, "");
    const characterCount = plainText.length;

    if (characterCount > CHARACTER_LIMIT_DESCRIPTION) {
      setError((prev) => ({
        ...prev,
        description: t("characterLimitExceeded", {
          limit: CHARACTER_LIMIT_DESCRIPTION,
          count: characterCount,
        }),
      }));
      return;
    } else {
      setError((prev) => ({
        ...prev,
        description: "",
      }));
    }

    if (hasNumberedList || hasBulletList) {
      const liRegex = /<li>(.*?)(?=<\/li>|$)/gi;
      const matches = newContent.match(liRegex);
      if (matches) {
        descriptions = matches?.map((match) => match.replace(/<\/?li>/gi, ""));
      }

      listType = hasNumberedList ? "ol" : "ul";

      const listHTML = `<${listType}>${descriptions
        ?.map((item) => `<li>${item}</li>`)
        .join("")}</${listType}>`;

      let leftoverText = newContent
        .replace(/<ol>.*?<\/ol>/gis, "")
        .replace(/<ul>.*?<\/ul>/gis, "")
        .trim();

      leftoverText = leftoverText
        .replace(/<p>/gi, "")
        .replace(/<\/p>/gi, "<br>");

      const finalDescription = leftoverText
        ? `${leftoverText}<br/>${listHTML}`
        : listHTML;

      setListingInput((prev) => ({
        ...prev,
        description: finalDescription,
      }));
    } else {
      setListingInput((prev) => ({
        ...prev,
        description: newContent.replace(/<p>/g, "").replace(/<\/p>/g, "<br>"),
      }));
    }

    setDescription(newContent);
  };

  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const isValidZipCode = (zipcode) => {
    const zipcodeRegex = /^\d{5}$/; // Matches exactly 5 digits
    return zipcodeRegex.test(zipcode);
  };
  const getErrorMessage = (name, value) => {
    if (
      [
        "latitude",
        "longitude",
        "email",
        "zipcode",
        "phone",
        "website",
        "address",
      ].includes(name)
    ) {
      switch (name) {
        case "latitude":
          // Latitude is required when POI mode is enabled
          if (hideMediaToggle && (!value || String(value).trim() === "")) {
            return t("fieldRequired");
          }
          if (!value) return "";
          const lat = parseFloat(value);
          if (isNaN(lat)) return t("pleaseEnterValidLatitude");
          if (lat < -90 || lat > 90) return t("latitudeRangeError");
          return "";

        case "longitude":
          // Longitude is required when POI mode is enabled
          if (hideMediaToggle && (!value || String(value).trim() === "")) {
            return t("fieldRequired");
          }
          if (!value) return "";
          const lng = parseFloat(value);
          if (isNaN(lng)) return t("pleaseEnterValidLongitude");
          if (lng < -180 || lng > 180) return t("longitudeRangeError");
          return "";

        case "email":
          if (!value) return "";
          return isValidEmail(value) ? "" : t("pleaseEnterValidEmail");

        case "zipcode":
          if (!value) return "";
          return isValidZipCode(value) ? "" : t("zipcodeInvalid");

        case "phone":
          if (!value) return "";
          const re = /^(?:\+49|0049|0)?\s?(\d{3})(?:[\s/-]?\d+)+$/;
          const digitsOnly = value.replace(/\D/g, "");
          if (
            !re.test(value) ||
            digitsOnly.length < 6 ||
            digitsOnly.length > 15
          )
            return t("pleaseEnterValidPhoneNumber");
          return "";
        // const regex = /^[0-9]{10}$/;
        // return regex.test(value) ? "" : t("pleaseEnterValidPhoneNumber");
        case "website":
          if (!value) return "";
          return validateURL(value) ? "" : t("validurl");
        case "address":
          // Address is only required when POI mode is enabled
          if (hideMediaToggle && (!value || value.trim() === "")) {
            return t("fieldRequired");
          }
          return "";
        default:
          return "";
      }
    }
    const field = selectedCategory?.metadata?.fields?.find(
      (f) => f.name === name
    );
    if (!field) return "";

    if (field.required && (!value || value.trim() === "")) {
      return t("fieldRequired");
    }

    if (field.type === "number" && isNaN(value)) {
      return t("pleaseEnterValidNumber");
    }

    return "";
  };

  const validateInput = (e) => {
    const { name, value } = e.target;
    const errorMessage = getErrorMessage(name, value);
    setError((prevState) => ({
      ...prevState,
      [name]: errorMessage,
    }));

    if (!value || value.trim() === "") return;

    const inputDate = new Date(value);

    if (name === "startDate" || name === "endDate") {
      const startDate =
        name === "startDate"
          ? inputDate
          : new Date(listingInput.additionalInformation.startDate);
      const endDate =
        name === "endDate"
          ? inputDate
          : new Date(listingInput.additionalInformation.endDate);

      if (startDate && endDate && startDate > endDate) {
        setError((prevState) => ({
          ...prevState,
          endDate: t("endDateBeforeStartDate"),
        }));
      } else {
        setError((prevState) => ({
          ...prevState,
          endDate: "",
        }));
      }
    }

    if (name === "expiryDate") {
      const expiryDate = new Date(value);
      const now = new Date();

      if (expiryDate && expiryDate < now) {
        setError((prevState) => ({
          ...prevState,
          expiryDate: t("expiryDateInPast"),
        }));
      } else {
        setError((prevState) => ({
          ...prevState,
          expiryDate: "",
        }));
      }
    }
  };

  const handleCategoryChange = async (event) => {
    const selectedCategoryId = parseInt(event.target.value, 10);
    setCategoryId(selectedCategoryId);
    const selectedCategory = categories.find(
      (category) => category.id === selectedCategoryId
    );
    setListingInput((prevInput) => ({
      ...prevInput,
      categoryId: selectedCategoryId,
    }));

    if (selectedCategory?.children?.length > 0) {
      setSubCategories(selectedCategory.children);
    } else {
      setSubCategories([]);
    }
    setChildrenId(0);
    const urlParams = new URLSearchParams(window.location.search);
    urlParams.set("categoryId", selectedCategoryId);
    urlParams.delete("subcategoryId", childrenId);
    const newUrl = `${window.location.pathname}?${urlParams.toString()}`;
    window.history.replaceState({}, "", newUrl);
  };

  const handleSelectCity = (city) => {
    if (selectedCities.some((selectedCity) => selectedCity.id === city.id)) {
      setError((prev) => ({
        ...prev,
        cityAlreadySelected: t("cityAlreadySelected"),
      }));
      setTimeout(
        () =>
          setError((prev) => ({
            ...prev,
            cityAlreadySelected: "",
          })),
        1000
      );
      return;
    }

    const updatedCities = [...selectedCities, city];

    const allCategoryLists = updatedCities?.map((c) =>
      (c.categoryData || [])
        .filter((cat) => cat.allowListingCreation !== 0) // 👈 FILTER OUT invalid ones
        ?.map((cat) => cat.id)
    );

    const commonCategoryIds = allCategoryLists.reduce((a, b) =>
      a.filter((id) => b.includes(id))
    );

    const allCategories = updatedCities.flatMap((c) => c.categoryData || []);
    const uniqueCategories = Array.from(
      new Map(
        allCategories
          .filter((cat) => cat.allowListingCreation !== 0) // 👈 Only allowed
          ?.map((cat) => [cat.id, cat])
      ).values()
    );

    const visibleCommonCategories = uniqueCategories.filter(
      (cat) =>
        commonCategoryIds.includes(cat.id) && !hiddenCategories.includes(cat.id)
    );

    setSelectedCities(updatedCities);
    setListingInput((prev) => ({
      ...prev,
      cityIds: updatedCities?.map((city) => city.id),
      categoryId: 0,
      childrenId: 0,
    }));
    setCategoryId(0);
    setChildrenId(0);
    setCategories(visibleCommonCategories);

    if (visibleCommonCategories.length === 0) {
      setError((prev) => ({
        ...prev,
        categoryId: t("categoryNotAvailableForSelectedCities"),
      }));
    } else {
      setError((prev) => ({
        ...prev,
        cityIds: "",
        cityAlreadySelected: "",
        categoryId: "",
      }));
    }
  };

  const handleRemoveCity = (cityId) => {
    const updatedCities = selectedCities.filter((city) => city.id !== cityId);
    setSelectedCities(updatedCities);
    setListingInput((prev) => ({
      ...prev,
      cityIds: updatedCities?.map((city) => city.id),
      categoryId: 0,
      childrenId: 0,
    }));
    setCategoryId(0);
    setChildrenId(0);

    if (updatedCities.length === 0) {
      setCategories([]);
      setError((prev) => ({
        ...prev,
        cityIds: t("pleaseSelectCity"),
        categoryId: "",
      }));
      return;
    }

    const allCategoryLists = updatedCities?.map((c) =>
      (c.categoryData || [])
        .filter((cat) => cat.allowListingCreation !== 0) // 👈 Important line
        ?.map((cat) => cat.id)
    );

    const commonCategoryIds = allCategoryLists.reduce((a, b) =>
      a.filter((id) => b.includes(id))
    );

    const allCategories = updatedCities.flatMap((c) => c.categoryData || []);
    const uniqueCategories = Array.from(
      new Map(
        allCategories
          .filter((cat) => cat.allowListingCreation !== 0)
          ?.map((cat) => [cat.id, cat])
      ).values()
    );

    const visibleCommonCategories = uniqueCategories.filter(
      (cat) =>
        commonCategoryIds.includes(cat.id) && !hiddenCategories.includes(cat.id)
    );

    setCategories(visibleCommonCategories);

    if (visibleCommonCategories.length === 0) {
      setError((prev) => ({
        ...prev,
        categoryId: t("categoryNotAvailableForSelectedCities"),
      }));
    } else {
      setError((prev) => ({
        ...prev,
        cityIds: "",
        categoryId: "",
      }));
    }
  };

  const selectedCategory = categories.find(
    (c) => c.id === listingInput.categoryId
  );

  useEffect(() => {
    const isCategorySpecificValid = selectedCategory?.metadata?.startDate
      ? listingInput.additionalInformation.startDate
      : true;
    //validate description
    const stripHtml = (html) =>
      html
        ?.replace(/<[^>]*>/g, "")
        .replace(/&nbsp;/g, "")
        .replace(/\s+/g, "")
        .trim();

    // Make description null-safe before stripping HTML
    const isDescriptionValid =
      stripHtml(listingInput.description || "")?.length > 0;

    const checkFormValidity = () => {
      const requiredFields = [
        listingInput.title,
        // Description is only required when hideMediaToggle is false
        hideMediaToggle ? true : isDescriptionValid,
        hideMediaToggle ? true : listingInput.description,
        categoryId,
        selectedCities.length > 0,
        // Address, latitude, and longitude are required when POI mode is enabled
        hideMediaToggle ? (listingInput.address && String(listingInput.address).trim() !== "") : true,
        hideMediaToggle ? (listingInput.latitude && String(listingInput.latitude).trim() !== "") : true,
        hideMediaToggle ? (listingInput.longitude && String(listingInput.longitude).trim() !== "") : true,
        !(
          error.title ||
          error.description ||
          error.cityIds ||
          error.categoryId ||
          (hideMediaToggle && error.address) ||
          (hideMediaToggle && error.latitude) ||
          (hideMediaToggle && error.longitude)
        ),
        isCategorySpecificValid,
      ];

      return requiredFields.every(Boolean);
    };

    const isValid = checkFormValidity();
    setIsFormValid(isValid);
  }, [listingInput, error, categoryId, selectedCities, hideMediaToggle, selectedCategory, categories]);
  function getUTCTime(dateString) {
    const date = new Date(dateString);
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, "0");
    const day = String(date.getUTCDate()).padStart(2, "0");
    const hours = String(date.getUTCHours()).padStart(2, "0");
    const minutes = String(date.getUTCMinutes()).padStart(2, "0");
    const formatted = `${year}-${month}-${day} ${hours}:${minutes}`;
    return formatted;
  }

  useEffect(() => {
    if (errorMessage) {
      const timer = setTimeout(() => {
        setErrorMessage("");
      }, 2000);

      return () => clearTimeout(timer); // 🧼 Cleanup on unmount or re-run
    }
  }, [errorMessage]);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage("");
      }, 2000);

      return () => clearTimeout(timer); // 🧼 Cleanup on unmount or re-run
    }
  }, [successMessage]);

  useEffect(() => {
    if (query.length < 3) {
      setSuggestions([]);
      return;
    }

    const fetchData = async () => {
      try {
        const response = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&q=${query}`
        );
        const data = await response.json();

        if (Array.isArray(data) && data.length > 0) {
          const validSuggestions = data.filter(
            (place) => place.lat && place.lon
          );
          setSuggestions(validSuggestions);
        } else {
          setSuggestions([]);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, [query]);

  const isHideMediaToggleDisabled = !newListing && hideMediaToggle;

  return (
    <section className="bg-gray-900 body-font relative min-h-screen">
      <SideBar />

      <div className="container w-auto px-5 py-2 bg-slate-900">
        <div className="bg-white mt-4 p-6 space-y-10">
          <div className="flex justify-between items-center">
            <h2
              style={{
                fontFamily:
                  "'Space Grotesk', Helvetica, Arial, Lucida, sans-serif",
              }}
              className="text-gray-900 text-lg mb-4 font-medium title-font"
            >
              {t(newListing ? "uploadPost" : "updatePost")}
            </h2>
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-700">
                {t("hideMedia") || "Hide Media"}
              </label>
              <button
                type="button"
                onClick={() => {
                  if (isHideMediaToggleDisabled) return;
                  const newToggleValue = !hideMediaToggle;
                  // If switching to POI mode (hideMediaToggle = true) and PDF exists, remove it
                  if (newToggleValue && pdf) {
                    handleRemovePDF();
                  }
                  // Clear latitude, longitude, and address when toggling POI mode
                  setListingInput((prev) => ({
                    ...prev,
                    latitude: "",
                    longitude: "",
                    address: "",
                  }));
                  setQuery("");
                  setSelectedLocation(null);
                  // Clear errors for these fields
                  setError((prev) => ({
                    ...prev,
                    latitude: "",
                    longitude: "",
                    address: "",
                  }));
                  setHideMediaToggle(newToggleValue);
                }}
                disabled={!newListing}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  hideMediaToggle ? "bg-gray-600" : "bg-gray-300"
                } ${!newListing ? "opacity-50 cursor-not-allowed" : ""}`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    hideMediaToggle ? "translate-x-6" : "translate-x-1"
                  }`}
                />
              </button>
            </div>
          </div>
          <div className="my-4 bg-gray-600 h-[1px]"></div>

          <div className="relative mb-0">
            <label
              htmlFor="title"
              className="block text-sm font-medium text-gray-900"
            >
              {t("title")} *
            </label>
            <input
              type="text"
              id="title"
              name="title"
              value={listingInput.title}
              onChange={onInputChange}
              onBlur={validateInput}
              required
              className="overflow-y:scroll border p-3 bg-white text-gray-800 border-gray-700 shadow-md placeholder:text-base duration-300 border-gray-300 rounded-lg w-full"
              placeholder={t("enterTitle")}
            />
            <div className="flex justify-between text-sm mt-1">
              <span
                className={`${
                  listingInput.title.replace(/(<([^>]+)>)/gi, "").length >
                  CHARACTER_LIMIT_TITLE
                    ? "mt-2 text-sm text-red-600"
                    : "mt-2 text-sm text-gray-500"
                }`}
              >
                {listingInput.title.replace(/(<([^>]+)>)/gi, "").length}/
                {CHARACTER_LIMIT_TITLE}
              </span>
              {error.title && (
                <span className="mt-2 text-sm text-red-600">{error.title}</span>
              )}
            </div>
          </div>

          <div className="mb-4">
            <SearchableDropdown
              label={
                process.env.REACT_APP_REGION_NAME === "HIVADA"
                  ? t("cluster")
                  : t("city")
              }
              required
              options={cities}
              selected={selectedCities}
              onSelect={handleSelectCity}
              onRemove={handleRemoveCity}
              sort="alphabetics"
              placeholder={t("select")}
            />
            <div
              className="mt-2 text-sm text-red-600"
              style={{
                visibility:
                  (selectedCities.length === 0 && error.cityIds) ||
                  error.cityAlreadySelected
                    ? "visible"
                    : "hidden",
              }}
            >
              {selectedCities.length === 0
                ? error.cityIds
                : error.cityAlreadySelected}
            </div>
          </div>

          {selectedCities.length > 0 && (
            <div className="relative mb-4">
              {categories.length > 0 ? (
                <>
                  <label
                    htmlFor="dropdown"
                    className="block text-sm font-medium text-gray-900"
                  >
                    {t("category")} *
                  </label>
                  <div className="relative">
                    <select
                      type="categoryId"
                      id="categoryId"
                      name="categoryId"
                      value={categoryId || 0}
                      onChange={handleCategoryChange}
                      required
                      className="overflow-y-scroll border p-3 bg-white text-gray-800 border-gray-700 shadow-md placeholder:text-base duration-300 border-gray-300 rounded-lg w-full appearance-none"
                    >
                      <option className="font-sans" value={0} key={0}>
                        {t("chooseOneCategory")}
                      </option>
                      {categories?.map((category) => (
                        <option
                          className="font-sans"
                          value={category.id}
                          key={category.id}
                        >
                          {t(category.name)}{" "}
                          {t(categoryDescription(category.id))}
                        </option>
                      ))}
                    </select>
                    <span className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                      ▼
                    </span>
                  </div>
                  {error.categoryId && (
                    <div className="mt-2 text-sm text-red-600">
                      {error.categoryId}
                    </div>
                  )}
                </>
              ) : (
                <div className="mt-2 text-sm text-red-600">
                  {t("categoryNotAvailableForSelectedCities")}
                </div>
              )}
            </div>
          )}

          {!hideMediaToggle && subCategories.length > 0 && (
            <div className="relative mb-4">
              <label
                htmlFor="childrenId"
                className="block text-sm font-medium text-gray-900"
              >
                {t("subCategory")}
              </label>
              <div className="relative">
                <select
                  id="childrenId"
                  name="childrenId"
                  value={childrenId || 0}
                  onChange={(e) => {
                    const childId = parseInt(e.target.value, 10);
                    setChildrenId(childId);
                    setListingInput((prev) => ({
                      ...prev,
                      childrenId: childId,
                    }));
                  }}
                  className="overflow-y:scroll border p-3 bg-white text-gray-800 border-gray-700 shadow-md placeholder:text-base duration-300 border-gray-300 rounded-lg w-full disabled:bg-gray-400 appearance-none"
                >
                  <option value={0}>{t("chooseSubcategory")}</option>
                  {subCategories.map((sub) => (
                    <option key={sub.id} value={sub.id}>
                      {t(sub.name)}
                    </option>
                  ))}
                </select>
                <span className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                  ▼
                </span>
              </div>
            </div>
          )}

          {selectedCategory?.metadata?.fields?.length > 0 && (
            <div className="py-2 grid grid-cols-1 md:grid-cols-2 gap-4">
              {selectedCategory.metadata.fields?.map((field) => {
                const lang = process.env.REACT_APP_LANG || "en";
                const label = field[lang] || field.name;
                const fieldName = field.name;
                const type = field.type;

                if (type === "date") {
                  return (
                    <div className="relative mb-0" key={fieldName}>
                      <label
                        htmlFor={fieldName}
                        className="block text-sm font-medium text-gray-900"
                      >
                        {label} {field.required && "*"}
                      </label>
                      <Flatpickr
                        id={fieldName}
                        name={fieldName}
                        value={
                          listingInput.additionalInformation[fieldName] || ""
                        }
                        options={{
                          enableTime: true,
                          dateFormat: "Y-m-d H:i",
                          minDate: listingId ? null : "today",
                          enable: [
                            function (date) {
                              const savedDateStr =
                                listingInput.additionalInformation[fieldName];
                              const savedDate = savedDateStr
                                ? new Date(savedDateStr)
                                : null;
                              const today = new Date();
                              today.setHours(0, 0, 0, 0);

                              return (
                                date >= today ||
                                (savedDate &&
                                  date.toDateString() ===
                                    savedDate.toDateString())
                              );
                            },
                          ],
                        }}
                        onChange={(date) => {
                          if (date && date[0]) {
                            const formatted = format(
                              date[0],
                              "yyyy-MM-dd'T'HH:mm"
                            );
                            setListingInput((prev) => ({
                              ...prev,
                              additionalInformation: {
                                ...prev.additionalInformation,
                                [fieldName]: formatted,
                              },
                            }));
                            validateInput({
                              target: { name: fieldName, value: formatted },
                            });
                          } else {
                            setListingInput((prev) => ({
                              ...prev,
                              additionalInformation: {
                                ...prev.additionalInformation,
                                [fieldName]: "",
                              },
                            }));
                            validateInput({
                              target: { name: fieldName, value: "" },
                            });
                          }
                        }}
                        className="border p-3 bg-white text-gray-800 border-gray-700 shadow-md placeholder:text-base duration-300 border-gray-300 rounded-lg w-full"
                      />
                      {error[fieldName] && (
                        <div className="mt-2 text-sm text-red-600">
                          {error[fieldName]}
                        </div>
                      )}
                    </div>
                  );
                }

                return (
                  <div className="relative mb-0" key={fieldName}>
                    <label
                      htmlFor={fieldName}
                      className="block text-sm font-medium text-gray-900"
                    >
                      {label} {field.required && "*"}
                    </label>
                    <input
                      id={fieldName}
                      name={fieldName}
                      type="text"
                      value={
                        listingInput.additionalInformation[fieldName] || ""
                      }
                      onChange={handleAdditionalInfoChange}
                      onBlur={validateInput}
                      className="border p-3 bg-white text-gray-800 border-gray-700 shadow-md placeholder:text-base duration-300 border-gray-300 rounded-lg w-full"
                    />
                    {error[fieldName] && (
                      <div className="mt-2 text-sm text-red-600">
                        {error[fieldName]}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {hideMediaToggle && (
            <div className="relative mb-0">
              <label
                htmlFor="title"
                className="block text-sm font-medium text-gray-900"
              >
                {t("address")} <span className="text-black">*</span>
              </label>

              <Combobox
                value={selectedLocation}
                onChange={(place) => {
                  if (place && place.display_name) {
                    // Option selected from suggestions
                    setSelectedLocation(place);
                    setQuery(place.display_name);
                    setListingInput((prev) => ({
                      ...prev,
                      longitude: place.lon, 
                      latitude: place.lat,
                      address: place.display_name,
                    }));
                    // Validate latitude and longitude after auto-populating them
                    // This ensures errors are cleared if values are valid
                    setTimeout(() => {
                      validateInput({ target: { name: "latitude", value: place.lat } });
                      validateInput({ target: { name: "longitude", value: place.lon } });
                      validateInput({ target: { name: "address", value: place.display_name } });
                    }, 0);
                  } else {
                    // Manual entry or null
                    setSelectedLocation(null);
                  }
                }}
              >
                <Combobox.Input
                  id="name"
                  name="name"
                  value={query}
                  required
                  className="overflow-y:scroll border p-3 bg-white text-gray-800 border-gray-700 shadow-md placeholder:text-base duration-300 rounded-lg w-full mb-1"
                  placeholder={t("entercityname")}
                  onChange={(event) => {
                    const newValue = event.target.value;
                    setQuery(newValue);
                    
                    // Clear selectedLocation when user types (allows manual entry)
                    if (selectedLocation && selectedLocation.display_name !== newValue) {
                      setSelectedLocation(null);
                      // Clear lat/lng when typing new address (user can enter manually)
                      setListingInput((prev) => ({
                        ...prev,
                        longitude: "",
                        latitude: "",
                      }));
                    }
                    
                    // Clear address when input is empty
                    if (!newValue) {
                      setListingInput((prev) => ({
                        ...prev,
                        address: "",
                        longitude: "",
                        latitude: "",
                      }));
                      // Don't show error during typing - only on submit
                    }
                  }}
                  onBlur={(event) => {
                    const typedAddress = event.target.value.trim();
                    
                    // If no option was selected, save the manually typed address
                    if (typedAddress && !selectedLocation) {
                      setListingInput((prev) => ({
                        ...prev,
                        address: typedAddress,
                      }));
                    }
                    // Don't show validation errors on blur - only on submit
                  }}
                  displayValue={(place) => place?.display_name || query}
                />
                {suggestions.length > 0 && (
                  <Combobox.Options
                    ref={queryCityNameRef}
                    className="max-h-60 overflow-y-auto border border-gray-300 rounded-md mb-4 mx-0"
                  >
                    {suggestions.map((place) => (
                      <Combobox.Option
                        key={place.place_id}
                        value={place}
                        className={({ active }) =>
                          `px-4 py-2 text-gray-900 cursor-pointer ${
                            active ? "bg-gray-100" : ""
                          }`
                        }
                      >
                        {place.display_name}
                      </Combobox.Option>
                    ))}
                  </Combobox.Options>
                )}
              </Combobox>

              <div className="flex justify-between text-sm mt-1">
                <span>
                  {query?.replace(/(<([^>]+)>)/gi, "").length}/
                  {CHARACTER_LIMIT_TITLE}
                </span>
                {error.address && (
                  <span className="text-sm text-red-600">{error.address}</span>
                )}
              </div>
            </div>
          )}

          <div className="relative mb-0">
            <div className="py-2 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="w-full">
                <label
                  htmlFor="latitude"
                  className="block text-sm font-medium text-gray-900"
                >
                  {t("Latitude")} {hideMediaToggle && <span className="text-black">*</span>}
                </label>
                <input
                  type="text"
                  id="latitude"
                  name="latitude"
                  value={listingInput.latitude}
                  onChange={onInputChange}
                  onBlur={validateInput}
                  className="border p-3 bg-white text-gray-800 border-gray-700 shadow-md placeholder:text-base duration-300 border-gray-300 rounded-lg w-full"
                  placeholder={t("enter_latitude")}
                />
                {error.latitude && (
                  <div className="mt-2 text-sm text-red-600">
                    {error.latitude}
                  </div>
                )}
              </div>

              <div className="w-full">
                <label
                  htmlFor="longitude"
                  className="block text-sm font-medium text-gray-900"
                >
                  {t("Longitude")} {hideMediaToggle && <span className="text-black">*</span>}
                </label>
                <input
                  type="text"
                  id="longitude"
                  name="longitude"
                  value={listingInput.longitude}
                  onChange={onInputChange}
                  onBlur={validateInput}
                  className="border p-3 bg-white text-gray-800 border-gray-700 shadow-md placeholder:text-base duration-300 border-gray-300 rounded-lg w-full"
                  placeholder={t("enter_longitude")}
                />
                {error.longitude && (
                  <div className="mt-2 text-sm text-red-600">
                    {error.longitude}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="relative mb-0">
            <label
              htmlFor="phone"
              className="block text-sm font-medium text-gray-900"
            >
              {process.env.REACT_APP_REGION_NAME === "HIVADA"
                ? t("personen")
                : t("phoneNumber")}
            </label>
            <input
              type="text" // Use text to get full control
              inputMode="numeric" // Show numeric keyboard on mobile
              pattern="[0-9]*"
              id="phone"
              name="phone"
              value={listingInput.phone}
              onChange={(e) => {
                if (
                  /^\d*$/.test(e.target.value) &&
                  e.target.value.length <= 15
                ) {
                  onInputChange(e);
                }
              }}
              onBlur={validateInput}
              className="border p-3 bg-white text-gray-800 border-gray-700 shadow-md placeholder:text-base duration-300 border-gray-300 rounded-lg w-full"
              placeholder={t("pleaseEnterPhone")}
            />
            <div
              className="mt-2 text-sm text-red-600"
              style={{
                visibility: error.phone ? "visible" : "hidden",
              }}
            >
              {error.phone}
            </div>
          </div>

          <div className="relative mb-0">
            <label
              htmlFor="place"
              className="block text-sm font-medium text-gray-900"
            >
              {t("email")}
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={listingInput.email}
              onChange={onInputChange}
              onBlur={validateInput}
              className="border p-3 bg-white text-gray-800 border-gray-700 shadow-md placeholder:text-base duration-300 border-gray-300 rounded-lg w-full"
              placeholder={t("emailExample")}
            />
            <div
              className="mt-2 text-sm text-red-600"
              style={{
                visibility: error.email ? "visible" : "hidden",
              }}
            >
              {error.email}
            </div>
          </div>

          <div className="relative mb-0">
            <label
              htmlFor="place"
              className="block text-sm font-medium text-gray-900"
            >
              {process.env.REACT_APP_REGION_NAME === "HIVADA"
                ? t("veranstaltungsinfos")
                : t("website")}
            </label>
            <input
              type="text"
              id="website"
              name="website"
              value={listingInput.website}
              onChange={onInputChange}
              onBlur={validateInput}
              className="border p-3 bg-white text-gray-800 border-gray-700 shadow-md placeholder:text-base duration-300 border-gray-300 rounded-lg w-full"
              placeholder={t("enter_website")}
            />
            <div
              className="mt-2 text-sm text-red-600"
              style={{
                visibility: error.website ? "visible" : "hidden",
              }}
            >
              {error.website}
            </div>
          </div>

          {(userRole === role.administrator ||
            userRole === role.superCityAdmin ||
            userRole === role.TerminalAdmin) &&
            newListing && (
              <div className="relative mb-0 mt-4">
                <div className="w-full">
                  <label
                    htmlFor="statusId"
                    className="block text-sm font-medium text-gray-900"
                  >
                    {t("Status")}
                  </label>
                  <div className="relative">
                    <select
                      id="statusId"
                      name="statusId"
                      value={listingInput.statusId}
                      onChange={onInputChange}
                      onBlur={validateInput}
                      className="overflow-y-scroll border p-3 bg-white text-gray-800 border-gray-700 shadow-md placeholder:text-base duration-300 border-gray-300 rounded-lg w-full appearance-none"
                    >
                      {Object.entries(status)?.map(([key, value]) => (
                        <option key={key} value={key}>
                          {t(value)}{" "}
                          {/* Using the status values translated with t() */}
                        </option>
                      ))}
                    </select>
                    <span className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                      ▼
                    </span>
                  </div>
                  <div
                    className="mt-2 text-sm text-red-600"
                    style={{
                      visibility: error.status ? "visible" : "hidden",
                    }}
                  >
                    {error.status}
                  </div>
                </div>
              </div>
            )}

          <div className="relative mb-0 mt-4">
            {/* <div className="w-full">
              <label htmlFor="zipcode" className="block text-sm font-medium text-gray-900">
                {t("zipcode")}
              </label>
              <input
                type="text"
                id="zipcode"
                name="zipcode"
                value={listingInput.zipcode}
                onChange={onInputChange}
                onBlur={validateInput}
                className="border p-3 bg-white text-gray-800 border-gray-700 shadow-md placeholder:text-base duration-300 border-gray-300 rounded-lg w-full"
                placeholder={t("enterPostal")}
              />
              <div
                className="mt-2 text-sm text-red-600"
                style={{
                  visibility: error.zipcode ? "visible" : "hidden",
                }}
              >
                {error.zipcode}
              </div>
            </div> */}

            <div
              className="mt-2 text-sm text-red-600"
              style={{
                visibility: error.status ? "visible" : "hidden",
              }}
            >
              {error.status}
            </div>
          </div>

          <div className="relative mb-4">
            <label
              htmlFor="description"
              className="block text-sm font-medium text-gray-900"
            >
              {t("description")} {!hideMediaToggle && "*"}
            </label>
            <ReactQuill
              type="text"
              id="description"
              name="description"
              ref={editor}
              value={description}
              onChange={(newContent) => onDescriptionChange(newContent)}
              onBlur={() => {
                const quillInstance = editor.current?.getEditor();
                if (quillInstance) {
                  validateInput({
                    target: {
                      name: "description",
                      value: quillInstance.root.innerHTML.replace(
                        /(<br>|<\/?p>)/gi,
                        ""
                      ),
                    },
                  });
                }
              }}
              placeholder={t("writeSomethingHere")}
              readOnly={updating || isSuccess}
              className="w-full bg-white rounded border border-gray-300 focus:border-black  text-base outline-none text-gray-700 py-0 px-0 leading-8 transition-colors duration-200 ease-in-out shadow-md"
            />
            <div className="flex justify-between text-sm mt-1">
              <span
                className={`${
                  description?.replace(/(<([^>]+)>)/gi, "")?.length >
                  CHARACTER_LIMIT_DESCRIPTION
                    ? "mt-2 text-sm text-red-600"
                    : "mt-2 text-sm text-gray-500"
                }`}
              >
                {description?.replace(/(<([^>]+)>)/gi, "")?.length}/
                {CHARACTER_LIMIT_DESCRIPTION}
              </span>
              {error.description && (
                <span className="mt-2 text-sm text-red-600">
                  {error.description}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="container w-auto px-5 py-2 base-bg-slate-900">
        <div className="bg-white mt-4 p-6 space-y-10">
          <h2 className="text-gray-900 text-lg mb-4 font-medium title-font">
            {t("uploadMedia")}
            <div className="my-4 bg-gray-600 h-[1px]"></div>
          </h2>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              {t("addFileHere")}
            </label>
            <div className="mt-2 text-sm text-green-600">
              {hideMediaToggle
                ? t("imageNumberAlertListings")
                : `${t("maxFileSizeAllert")} & ${t(
                    "imageNumberAlertListings"
                  )}`}
            </div>

            <div
              className={`mt-1 flex justify-center rounded-md border-2 border-dashed border-black px-6 pt-5 pb-6 bg-slate-200`}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
            >
              {image && image.length > 0 && newListing ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <FormImage
                    updateImageList={setImage}
                    handleRemoveImage={handleRemoveImage}
                    image={image}
                    localImageOrPdf={localImageOrPdf}
                    localImages={localImages}
                  />
                  {image.length < 8 && (
                    <label
                      htmlFor="file-upload"
                      className={`object-cover h-64 w-full m-4 rounded-xl ${
                        image.length < 8 ? "bg-slate-200" : ""
                      }`}
                    >
                      <div className="h-full flex items-center justify-center">
                        <div className="text-8xl text-black">+</div>
                      </div>
                      <input
                        id="file-upload"
                        type="file"
                        accept={hideMediaToggle ? "image/*" : "image/*,.pdf"}
                        className="sr-only"
                        onChange={handleMultipleInputChange}
                        multiple
                      />
                    </label>
                  )}
                </div>
              ) : image &&
                Array.isArray(image) &&
                image.length === 1 &&
                typeof image[0] === "string" &&
                image[0].includes("admin/") ? (
                <div>
                  <FormImage
                    updateImageList={setImage}
                    handleRemoveImage={handleRemoveImage}
                    handleInputChange={handleInputChange}
                    image={image}
                    localImageOrPdf={localImageOrPdf}
                    localImages={localImages}
                  />
                  {image.length < 8 && (
                    <label
                      htmlFor="file-upload"
                      className={`object-cover h-64 w-full mb-4 rounded-xl ${
                        image.length < 8 ? "bg-slate-200" : ""
                      }`}
                    >
                      <div className="h-full flex items-center justify-center">
                        <div className="text-8xl text-black">+</div>
                      </div>
                      <input
                        id="file-upload"
                        type="file"
                        accept={hideMediaToggle ? "image/*" : "image/*,.pdf"}
                        className="sr-only"
                        onChange={handleUpdateMultipleInputChange}
                        multiple
                      />
                    </label>
                  )}
                </div>
              ) : image &&
                Array.isArray(image) &&
                image.length > 0 &&
                !newListing ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <FormImage
                    updateImageList={setImage}
                    handleRemoveImage={handleRemoveImage}
                    handleInputChange={handleInputChange}
                    image={image}
                    localImageOrPdf={localImageOrPdf}
                    localImages={localImages}
                  />
                  {image.length < 8 && (
                    <label
                      htmlFor="file-upload"
                      className={`object-cover h-64 w-full mb-4 rounded-xl ${
                        image.length < 8 ? "bg-slate-200" : ""
                      }`}
                    >
                      <div className="h-full flex items-center justify-center">
                        <div className="text-8xl text-black">+</div>
                      </div>
                      <input
                        id="file-upload"
                        type="file"
                        accept={hideMediaToggle ? "image/*" : "image/*,.pdf"}
                        className="sr-only"
                        onChange={handleUpdateMultipleInputChange}
                        multiple
                      />
                    </label>
                  )}
                </div>
              ) : pdf ? (
                <div className="flex flex-col items-center">
                  {/* PDF Preview */}
                  {pdf.link ? (
                    <iframe
                      src={pdf.link}
                      width="100%"
                      height="500px" // Adjust height as needed
                      style={{ border: "none" }}
                      title="PDF Preview"
                    >
                      <p>
                        Your browser does not support PDF preview.{" "}
                        <a href={pdf.link}>Download the PDF</a> instead.
                      </p>
                    </iframe>
                  ) : pdf.name ? (
                    <p className="mt-2">
                      <a
                        target="_blank"
                        rel="noreferrer"
                        href={pdf.link}
                        className="text-blue-600 hover:underline"
                      >
                        {pdf.name.split("?")[0]}{" "}
                        {/* Remove query parameters from the name */}
                      </a>
                    </p>
                  ) : null}

                  {/* Remove Button */}
                  <button
                    className="bg-black font-md text-gray-300 shadow-lg p-2 text-white rounded-lg w-full py-2 px-4 mt-2"
                    onClick={handleRemovePDF}
                  >
                    {t("removeFile")}
                  </button>
                </div>
              ) : (
                <div className="text-center">
                  <UploadSVG />
                  <p className="mt-1 text-sm text-gray-900">
                 {/* { {t("dragAndDropImageForPoi")}  {t("dragAndDropImageOrPDF")}} */}

                  {hideMediaToggle
                ? `${t("dragAndDropImageForPoi")}`
                : `${t("dragAndDropImageOrPDF")}`}
                  </p>
                  <div className="relative mb-0 mt-8">
                    <label
                      className={`bg-black font-md text-gray-300 shadow-lg p-2 text-white rounded-lg w-full py-2 px-4`}
                    >
                      <span className="button-label">{t("upload")}</span>
                      <input
                        id="file-upload"
                        type="file"
                        accept={hideMediaToggle ? "image/*" : "image/*,.pdf"}
                        className="sr-only"
                        onChange={handleInputChange}
                        multiple
                      />
                    </label>
                  </div>
                </div>
              )}
            </div>

            <div className="mt-2 text-sm text-green-600">
             { hideMediaToggle ?  `${t("imagePoiWarning")}`  : `${t("imagePdfWarning")}`  }
            </div>
          </div>
        </div>
      </div>

      <div className="container w-auto px-5 py-2 bg-slate-900">
        <div className="bg-white mt-4 p-6">
          <div className="py-2 mt-1 px-2">
            <p className="pb-2">
              {process.env.REACT_APP_NAME === "WALDI APP"
                ? t(
                    "byUploadingIConfirmTheTermsOfUseInParticularThatIHaveTheRightsToPublishTheContent"
                  )
                : ""}
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleSubmit}
                disabled={!isFormValid || updating || isSuccess}
                className="bg-black font-md text-gray-300 shadow-lg p-2 text-white rounded-lg w-full disabled:opacity-60"
              >
                {newListing ? t("uploadPost") : t("saveChanges")}
                {updating && (
                  <svg
                    aria-hidden="true"
                    className="inline w-5 h-5 ml-2 text-gray-200 animate-spin dark:text-gray-900 fill-gray-600 dark:fill-gray-300"
                    viewBox="0 0 100 101"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                      fill="currentColor"
                    />
                    <path
                      d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                      fill="currentFill"
                    />
                  </svg>
                )}
              </button>

              {!newListing && (
                <button
                  type="button"
                  onClick={handleCancel}
                  className="w-full bg-red-700 font-md text-gray-300 shadow-lg p-2 text-white rounded-lg"
                >
                  {t("cancel")}
                </button>
              )}
              {newListing && (
                <button
                  type="button"
                  onClick={() => navigate(-1)} // Inline navigation
                  className="w-full bg-red-700 font-md text-gray-300 shadow-lg p-2 text-white rounded-lg"
                >
                  {t("Back")}
                </button>
              )}
            </div>
          </div>
          <div className="py-2 mt-1 px-2">
            {successMessage && (
              <Alert type={"success"} message={successMessage} />
            )}
            {errorMessage && <Alert type={"danger"} message={errorMessage} />}
          </div>
        </div>
      </div>
    </section>
  );
}

export default UploadListings;
